import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { brandingAPI, resolveAssetUrl, publicEventIcsUrl } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import QRCode from 'react-qr-code';
import { 
  Calendar,
  CalendarPlus,
  Clock,
  MapPin,
  CheckCircle,
  Loader2,
  Target,
  User,
  BookOpen
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function PublicRegister() {
  const { inviteCode } = useParams();
  const [event, setEvent] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [registered, setRegistered] = useState(false);
  const [attendeeId, setAttendeeId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    provider_number: '',
    diet_type: '',
    diet_notes: '',
  });

  useEffect(() => {
    fetchData();
  }, [inviteCode]);

  const fetchData = async () => {
    try {
      const [eventRes, brandingRes] = await Promise.all([
        axios.get(`${API}/public/events/${inviteCode}`),
        brandingAPI.getPublic()
      ]);
      setEvent(eventRes.data);
      setBranding(brandingRes.data);
    } catch (error) {
      toast.error('Event not found or link expired');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const response = await axios.post(`${API}/public/events/${inviteCode}/register`, formData);
      setRegistered(true);
      setAttendeeId(response.data.id);
      toast.success('Registration successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setSubmitting(false);
    }
  };

  const orgName = branding?.organization_name || 'Illawarra Indian Doctors Association';

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <img
              src="/iida-logo.png"
              alt="IIDA"
              className="h-20 w-20 object-contain mx-auto mb-4"
            />
            <h2 className="text-xl font-semibold mb-2">Event Not Found</h2>
            <p className="text-muted-foreground">
              This event link may have expired or is invalid.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (registered) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Toaster richColors position="top-center" />
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <img
              src={branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png'}
              alt={orgName}
              className="h-20 w-20 object-contain mx-auto mb-4"
            />
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-6 h-6 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold mb-2">Registration Confirmed!</h2>
            <p className="text-muted-foreground mb-4">
              You're registered for <strong>{event.title}</strong>
            </p>
            <div className="text-sm text-muted-foreground space-y-1">
              <p>{format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}</p>
              <p>{event.time} • {event.venue}</p>
            </div>

            {/* Personal check-in QR code — admin scans this on the day to check you in */}
            {attendeeId && (
              <div
                className="mt-6 p-4 rounded-lg border border-border bg-card"
                data-testid="personal-checkin-qr-block"
              >
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">
                  Your personal check-in QR
                </p>
                <div className="bg-white p-3 inline-block rounded">
                  <QRCode
                    value={attendeeId}
                    size={160}
                    data-testid="personal-checkin-qr"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-3">
                  Show this code at the registration desk on event day. As soon as we check you in,
                  your Certificate of Attendance will be emailed to you automatically.
                </p>
              </div>
            )}

            <a
              href={publicEventIcsUrl(inviteCode)}
              className="inline-flex items-center justify-center gap-2 mt-4 px-4 py-2 rounded-lg border border-border text-sm font-medium hover:bg-muted transition-colors"
              data-testid="add-to-calendar-btn"
            >
              <CalendarPlus className="w-4 h-4" />
              Add to Calendar (.ics)
            </a>
            <div className="mt-6 p-4 bg-muted rounded-lg text-sm text-left">
              <p className="font-medium mb-2">What's Next?</p>
              <ul className="space-y-1 text-muted-foreground">
                <li>• Arrive at the venue on time</li>
                <li>• Present your QR code above for check-in</li>
                <li>• Your certificate will be emailed to you automatically on check-in</li>
                <li>• Please complete the feedback form to help us improve future events</li>
              </ul>
            </div>
            <p className="text-xs text-muted-foreground mt-4">
              Your certificate will be emailed to you automatically when we check you in on event day.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Toaster richColors position="top-center" />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-3">
          <img
            src={branding?.logo_url ? resolveAssetUrl(branding.logo_url) : '/iida-logo.png'}
            alt={orgName}
            className="h-16 w-auto object-contain"
            data-testid="public-register-logo"
          />
          <div>
            <h1 className="font-semibold text-foreground">{orgName}</h1>
            <p className="text-xs text-muted-foreground">Educational Activity</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Event Info */}
          <div>
            <h2 className="text-3xl font-bold text-foreground mb-4" data-testid="event-title">
              {event.title}
            </h2>
            
            {event.description && (
              <p className="text-muted-foreground mb-6">{event.description}</p>
            )}

            <div className="space-y-3 mb-8">
              <div className="flex items-center gap-3 text-foreground">
                <Calendar className="w-5 h-5 text-muted-foreground" />
                <span>{format(parseISO(event.date), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <Clock className="w-5 h-5 text-muted-foreground" />
                <span>{event.time} • {event.duration_hours} hours</span>
              </div>
              <div className="flex items-center gap-3 text-foreground">
                <MapPin className="w-5 h-5 text-muted-foreground" />
                <span>{event.venue}</span>
              </div>
            </div>

            {/* Speakers */}
            {event.speakers?.length > 0 && (
              <Card className="border-border mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Speakers
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {event.speakers.map((speaker, index) => (
                      <div key={index} className="text-sm">
                        <span className="font-medium">{speaker.name}</span>
                        {(speaker.title || speaker.organization) && (
                          <span className="text-muted-foreground">
                            {' - '}{[speaker.title, speaker.organization].filter(Boolean).join(', ')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Topics */}
            {event.topics?.length > 0 && event.topics.some(t => t.trim()) && (
              <Card className="border-border mb-6">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <BookOpen className="w-4 h-4" />
                    Topics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-1">
                    {event.topics.filter(t => t.trim()).map((topic, index) => (
                      <li key={index} className="text-sm text-muted-foreground flex gap-2">
                        <span>•</span>
                        <span>{topic}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Learning Objectives */}
            {event.learning_objectives?.length > 0 && event.learning_objectives.some(o => o.trim()) && (
              <Card className="border-border">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Target className="w-4 h-4" />
                    Learning Objectives
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground mb-3">
                    By the end of this session, participants will be able to:
                  </p>
                  <ul className="space-y-2">
                    {event.learning_objectives.filter(o => o.trim()).map((obj, index) => (
                      <li key={index} className="flex gap-2 text-sm">
                        <span className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs">
                          {index + 1}
                        </span>
                        <span className="text-muted-foreground">{obj}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Registration Form */}
          <div>
            <Card className="border-border sticky top-6">
              <CardHeader>
                <CardTitle>Register for this Event</CardTitle>
                <CardDescription>
                  Fill in your details to secure your spot
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      placeholder="Dr. John Smith"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="h-11"
                      data-testid="register-name-input"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john.smith@example.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="h-11"
                      data-testid="register-email-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Your certificate will be sent to this email
                    </p>
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="provider_number">Provider Number (Optional)</Label>
                    <Input
                      id="provider_number"
                      placeholder="e.g., 1234567A"
                      value={formData.provider_number}
                      onChange={(e) => setFormData({ ...formData, provider_number: e.target.value })}
                      className="h-11"
                      data-testid="register-provider-input"
                    />
                  </div>

                  {/* Dietary preferences */}
                  <div
                    className="space-y-3 rounded-lg border border-border bg-muted/30 p-3"
                    data-testid="register-diet-block"
                  >
                    <Label className="text-sm font-medium">Dietary preference (for catering)</Label>
                    <div className="flex gap-4 flex-wrap">
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="diet_type"
                          checked={formData.diet_type === 'veg'}
                          onChange={() => setFormData({ ...formData, diet_type: 'veg' })}
                          data-testid="register-diet-veg"
                        />
                        <span className="text-sm">Vegetarian</span>
                      </label>
                      <label className="inline-flex items-center gap-2 cursor-pointer">
                        <input
                          type="radio"
                          name="diet_type"
                          checked={formData.diet_type === 'non_veg'}
                          onChange={() => setFormData({ ...formData, diet_type: 'non_veg' })}
                          data-testid="register-diet-nonveg"
                        />
                        <span className="text-sm">Non-vegetarian</span>
                      </label>
                      {formData.diet_type && (
                        <button
                          type="button"
                          className="text-xs text-muted-foreground underline ml-auto"
                          onClick={() => setFormData({ ...formData, diet_type: '' })}
                          data-testid="register-diet-clear"
                        >
                          clear
                        </button>
                      )}
                    </div>
                    <div className="space-y-1">
                      <Label htmlFor="diet_notes" className="text-xs text-muted-foreground">
                        Other requirements (optional)
                      </Label>
                      <Input
                        id="diet_notes"
                        placeholder="e.g., Halal, no fish, no meat, nut allergy"
                        value={formData.diet_notes}
                        onChange={(e) => setFormData({ ...formData, diet_notes: e.target.value })}
                        className="h-10"
                        maxLength={200}
                        data-testid="register-diet-notes-input"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 mt-2"
                    disabled={submitting}
                    data-testid="register-submit-btn"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      'Register Now'
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>

            {event.educational_statement && (
              <p className="text-xs text-muted-foreground text-center mt-4 italic">
                {event.educational_statement}
              </p>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-12">
        <div className="max-w-4xl mx-auto px-6 py-6 text-center text-sm text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} {orgName}</p>
        </div>
      </footer>
    </div>
  );
}
